// useMe — fetches the current user from auth.whoami. The shell gates
// auth-required affordances on me?.username being present; the user
// menu + account panel render from the same data.
//
// v51 — extended to expose role + avatar fields so the section router
// can swap in kid views and the user menu / topbar can render the
// per-account emoji and color.
//
// Returns null while loading or unauthenticated. On 401 the global
// rpc:unauthorized handler in app.tsx swaps in the login interstitial,
// so this hook just leaves `me` null and lets the shell handle it.

import { useEffect, useState } from "preact/hooks";
import { api } from "./rpc";

export interface Me {
  username: string;
  initial: string;
  role: "admin" | "user" | "kid";
  email?: string;
  /** Friendly display name — defaults to username if unset. */
  displayName: string;
  /** OKLCH expression for the avatar / per-account accent. Null when
   *  unset (renders with a default gradient). */
  avatarColor: string | null;
  /** Single grapheme used as the avatar mascot across kid + parent
   *  surfaces. Null when unset. */
  avatarEmoji: string | null;
  /** Kid age — null on adult accounts. */
  age: number | null;
  /** Session scope. "kid" sessions are issued by /api/kid-login and
   *  trigger the kid-only nav + tool filter. */
  scope: string;
}

/**
 * @param authStatus  Optional auth state. Pass `auth.status` so the hook
 *   refetches whoami when the session flips (e.g. sign-out → kid PIN
 *   login should swap the cached admin profile for the kid's). Omit
 *   for one-shot usage where the caller doesn't care about re-auth.
 * @param sessionKey  Optional extra dependency that changes when the
 *   session changes *without* authStatus changing — notably the
 *   kid-device → kid flip after a PIN login, where status stays
 *   "logged-in" both sides. Pass `auth.info?.scope` (or any value that
 *   moves on re-auth) so whoami re-fetches and `me.scope` updates;
 *   otherwise the shell stays stuck on the previous scope's view.
 */
export function useMe(authStatus?: string, sessionKey?: string): Me | null {
  const [me, setMe] = useState<Me | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Drop stale profile immediately when the session goes away; the
    // adult shell uses `me === null` as a "still loading" check too,
    // but for the explicit logged-out case we want the next logged-in
    // transition to trigger a fresh fetch rather than reusing the old
    // me cached from the previous session.
    if (authStatus === "logged-out") {
      setMe(null);
      return;
    }
    api.authWhoami()
      .then((row) => {
        if (cancelled) return;
        // Treat server-side role === "admin" as our legacy "admin"
        // value (everything outside kid views still keys off `role`
        // being one of "admin" / "user" / "kid").
        const role: Me["role"] = row.role === "kid" ? "kid" : (row.role === "admin" || row.isAdmin) ? "admin" : "user";
        setMe({
          username: row.id,
          initial: (row.displayName?.[0] || row.id[0] || "?").toUpperCase(),
          role,
          email: row.email || undefined,
          displayName: row.displayName,
          avatarColor: row.avatarColor,
          avatarEmoji: row.avatarEmoji,
          age: row.age,
          scope: row.scope,
        });
      })
      .catch(() => {
        if (!cancelled) setMe(null);
      });
    return () => { cancelled = true; };
  }, [authStatus, sessionKey]);

  return me;
}
